import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import CloudErrorBoundary from './components/CloudErrorBoundary';
import { logErrorToCloud } from './tools/cloudLogProxy';

/**
 * After a deploy, cached HTML may reference removed JS chunks (404). One full reload usually fixes it.
 * We allow a single auto-reload per "episode"; the flag is cleared a few seconds after window load so
 * later navigations/deploys in the same tab can trigger recovery again.
 */
const CHUNK_RELOAD_SESSION_KEY = "app_chunk_autoreload_v1";

function isLikelyStaleChunkError(reason) {
  const msg = reason?.message != null ? String(reason.message) : String(reason || "");
  const name = reason?.name != null ? String(reason.name) : "";
  if (name === "ChunkLoadError") return true;
  if (/ChunkLoadError/i.test(msg)) return true;
  if (/Loading chunk [\d]+ failed/i.test(msg)) return true;
  if (/Failed to fetch dynamically imported module/i.test(msg)) return true;
  if (/Importing a module script failed/i.test(msg)) return true;
  return false;
}

function tryReloadOnceAfterStaleChunk(reason) {
  if (!isLikelyStaleChunkError(reason)) return false;
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY)) return false;
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, "1");
  } catch (_) {
    return false;
  }
  window.location.reload();
  return true;
}

if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    window.setTimeout(() => {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
      } catch (_) {}
    }, 2500);
  });
}

// Browsers may emit this when ResizeObserver runs in the same frame as layout changes; it is not actionable.
// Stopping propagation keeps the CRA dev overlay from treating it as a fatal runtime error.
if (typeof window !== "undefined") {
  window.addEventListener(
    "error",
    (event) => {
      const msg = event?.message || event?.error?.message || "";
      if (
        typeof msg === "string" &&
        (msg.includes("ResizeObserver loop completed with undelivered notifications") ||
          msg.includes("ResizeObserver loop limit exceeded"))
      ) {
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

// Enforce HTTPS in production-like environments (except localhost)
// Temporarily disabled per request
// if (window.location.protocol === 'http:' && !/^(localhost|127\.0\.0\.1)/.test(window.location.hostname)) {
//   window.location.href = 'https://' + window.location.host + window.location.pathname + window.location.search + window.location.hash;
// }

const root = ReactDOM.createRoot(document.getElementById('root'));

// Global error hooks for "real" uncaught errors.
// These are best-effort and intentionally never throw.
try {
  // JS runtime errors
  window.addEventListener("error", (event) => {
    // event.error is usually an Error; for script errors it may be undefined.
    logErrorToCloud(event?.error || event?.message, {
      source: "window_error",
      filename: event?.filename,
      lineno: event?.lineno,
      colno: event?.colno,
    });
  });

  // Resource loading errors (script/css/img/audio/video/etc).
  // These do NOT bubble reliably, so capture=true is required.
  window.addEventListener(
    "error",
    (event) => {
      try {
        const target = event?.target;
        // Filter out the runtime-error case (where target is window).
        if (!target || target === window) return;
        const tagName = (target.tagName || "").toLowerCase();
        const url = target.currentSrc || target.src || target.href;
        if (!url) return;

        logErrorToCloud(`Resource failed to load: ${tagName || "resource"} ${url}`, {
          source: "resource_error",
          tagName,
          url,
        });
      } catch (_) {}
    },
    true
  );

  // Browser security policy violations (CSP)
  window.addEventListener("securitypolicyviolation", (event) => {
    try {
      logErrorToCloud(`CSP violation: ${event?.violatedDirective || "unknown"}`, {
        source: "securitypolicyviolation",
        violatedDirective: event?.violatedDirective,
        effectiveDirective: event?.effectiveDirective,
        blockedURI: event?.blockedURI,
        disposition: event?.disposition,
        originalPolicy: event?.originalPolicy,
        statusCode: event?.statusCode,
        documentURI: event?.documentURI,
        referrer: event?.referrer,
        lineNumber: event?.lineNumber,
        columnNumber: event?.columnNumber,
        sample: event?.sample,
      });
    } catch (_) {}
  });

  // User-environment issues (best-effort)
  window.addEventListener("offline", () => {
    try {
      logErrorToCloud("Browser went offline", { source: "offline" });
    } catch (_) {}
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (tryReloadOnceAfterStaleChunk(event.reason)) {
      event.preventDefault();
      return;
    }
    logErrorToCloud(event?.reason, { source: "unhandledrejection" });
  });
} catch (_) {}

root.render(
    <CloudErrorBoundary>
      <App />
    </CloudErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
